import React, { useState } from 'react';
import Modal from '~/components/organism/ModalsV2/Modal';
import Overlay from '~/components/organism/ModalsV2/Overlay';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Button from '~/components/atom/Button';
import Textarea from '~/components/molecule/Textarea';
import { Body, Heading3 } from '~/components/atom/Typography';
import Divider from '~/components/atom/Divider';
import Icon from '~/components/atom/Icon';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import { useStartLfaRequestMutation } from '~/graphql/types';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import Catalog from '~/Catalog';

type Props = {
  contactId: string;
  eventId: string;
  onClose: () => void;
  onSuccess?: () => void;
  hasOpenRequest: boolean;
};

const text = {
  title: 'Start LFA Request',
  note: 'Notitie',
  notePlaceholder: 'Voer een notitie in...',
  cancel: Catalog.cancel,
  submit: 'Start Request',
  error: 'Er is al een open LFA request voor dit contact',
  success: 'LFA request succesvol gestart',
  errorMessage: Catalog.genericUnknownErrorMessage,
};

const StartLfaRequestModal: React.FC<Props> = ({
  contactId,
  eventId,
  onClose,
  onSuccess,
  hasOpenRequest,
}) => {
  const account = useCurrentAccount();
  const addToast = useAddToast();
  const [note, setNote] = useState<string | null>(null);

  const [startLfaRequest, { loading }] = useStartLfaRequestMutation({
    variables: {
      accountId: account.id,
      contactId,
      eventId,
      note: note || undefined,
    },
    onCompleted: data => {
      if (data?.startLFARequest) {
        addToast([formatToastMessage(text.success, 'success')]);
        onSuccess?.();
        onClose();
      }
    },
    onError: error => {
      addToast([
        formatToastMessage(error.message || text.errorMessage, 'danger'),
      ]);
    },
  });

  const handleSubmit = async () => {
    if (hasOpenRequest) {
      addToast([formatToastMessage(text.error, 'danger')]);
      return;
    }

    await startLfaRequest();
  };

  return (
    <Overlay onClose={onClose}>
      <Modal maxWidth="500px" dataTestId="start-lfa-request-modal">
        <JustificationContainer
          direction="column"
          padding={['base']}
          border={{ radius: 'base' }}
          width="100%"
        >
          <JustificationContainer
            direction="column"
            gap="base"
            backgroundColor={{ group: 'white' }}
            border={{ radius: 'base' }}
            padding={['base']}
            width="100%"
          >
            <Heading3 size="m" margin={[null]}>
              <JustificationContainer
                direction="row"
                gap="xs"
                align="center"
                justification="center"
              >
                <Icon name="user" strokeWidth={2.3} />
                {text.title}
              </JustificationContainer>
            </Heading3>
            <Divider margin={[null]} />

            <JustificationContainer direction="column" gap="base" width="100%">
              {hasOpenRequest && (
                <Body
                  size="base"
                  color={{ group: 'danger' }}
                  margin={[null, null, 's', null]}
                >
                  {text.error}
                </Body>
              )}

              <Textarea
                name="note"
                label={text.note}
                value={note}
                onChange={setNote}
                placeholder={text.notePlaceholder}
                dataTestId="lfa-request-note-input"
              />
            </JustificationContainer>

            <JustificationContainer
              direction="row"
              justification="end"
              width="100%"
              gap="base"
              margin={['base', null, null, null]}
            >
              <Button
                label={text.cancel}
                appearance="danger"
                onClick={onClose}
                ghost
                loading={loading}
              />
              <Button
                label={text.submit}
                appearance="secondary"
                onClick={handleSubmit}
                disabled={hasOpenRequest}
                loading={loading}
                dataTestId="start-lfa-request-submit"
              />
            </JustificationContainer>
          </JustificationContainer>
        </JustificationContainer>
      </Modal>
    </Overlay>
  );
};

export default StartLfaRequestModal;
